import type { FunctionComponent } from 'react';
import React, { Suspense, useCallback, useEffect, useState } from 'react';
import { IntlProvider } from 'react-intl-next';

import FabricAnalyticsListeners from '@atlaskit/analytics-listeners';
import { AnalyticsContext } from '@atlaskit/analytics-next';
import type { CardProps as SmartCardProps } from '@atlaskit/smart-card';
import { Analytics } from '@trello/atlassian-analytics';
import { ChunkLoadErrorBoundary } from '@trello/error-reporting';
import { useLanguage } from '@trello/locale';
import type { UGCString } from '@trello/privacy';
import { isTrelloUrl } from '@trello/urls';
import { useLazyComponent } from '@trello/use-lazy-component';

import type { SmartCardClientResponse } from './SmartCardClient';
import { smartCardClient } from './SmartCardClient';
import type { SmartCardProviderProps } from './SmartCardProvider';
import { SmartCardProvider } from './SmartCardProvider';
import { useSmartCardLocale } from './useSmartCardLocale';
import { useSmartCardProviderRefreshKey } from './useSmartCardProviderRefreshKey';

interface AnalyticsContextDataType {
  attributes: {
    component?: string;
    definitionId?: string;
    accessType?: string | UGCString;
    cloudId?: string;
    urlType?: string | UGCString;
  };
}

interface Props {
  url: string;
  plainLink: () => JSX.Element;
  smartCardProps: SmartCardProps;
  onLinkResolved?: (resolved: SmartCardClientResponse) => void;
  onLinkUnresolved?: () => void;
  onSmartCardClick?: () => void;
  shouldRender: boolean;
  analyticsContextData: AnalyticsContextDataType;
  handleTrelloLinkClick: (e: MouseEvent, linkElem: HTMLAnchorElement) => void;
  smartCardProviderFeatureFlags?: SmartCardProviderProps['featureFlags'];
}

export const SmartCardWrapper: FunctionComponent<Props> = ({
  url,
  plainLink,
  onSmartCardClick,
  onLinkResolved,
  onLinkUnresolved,
  smartCardProps,
  shouldRender = false,
  analyticsContextData,
  handleTrelloLinkClick,
  smartCardProviderFeatureFlags,
}) => {
  const { language } = useLanguage();
  const { messages, isLocaleReady } = useSmartCardLocale();

  const SmartCard = useLazyComponent(
    () =>
      import(
        /* webpackChunkName: "atlaskit-smart-card" */ '@atlaskit/smart-card'
      ),
    {
      preload: false,
      namedImport: 'Card',
    },
  );

  const [resolvedUrl, setResolvedUrl] = useState<
    SmartCardClientResponse | undefined
  >();

  useEffect(() => {
    const checkIfSmartCard = async () => {
      const resolved = await smartCardClient.fetchData(url).catch(() => {});
      setResolvedUrl(resolved ?? undefined);

      if (resolved) {
        onLinkResolved?.(resolved);
      } else {
        onLinkUnresolved?.();
      }
    };

    checkIfSmartCard();
  }, [url, onLinkResolved, onLinkUnresolved]);

  const onClick = useCallback(
    (e) => {
      const isModifierKeyPressedOnEvent =
        e.altKey || e.ctrlKey || e.metaKey || e.shiftKey;
      const shouldNavigateInApp =
        isTrelloUrl(url) && !isModifierKeyPressedOnEvent;

      shouldNavigateInApp
        ? handleTrelloLinkClick(e, e.currentTarget)
        : window.open(url, '_blank');

      onSmartCardClick?.();
    },
    [handleTrelloLinkClick, onSmartCardClick, url],
  );

  const refreshKey = useSmartCardProviderRefreshKey(resolvedUrl);

  if (!shouldRender || !isLocaleReady) {
    return plainLink();
  }

  return (
    <ChunkLoadErrorBoundary fallback={plainLink()}>
      <Suspense fallback={plainLink()}>
        <FabricAnalyticsListeners
          // This only fails in strict mode, so when this is compiled by `app/`,
          // `@ts-expect-error` won't detect any errors.
          // eslint-disable-next-line @typescript-eslint/prefer-ts-expect-error
          // @ts-ignore
          client={Analytics.dangerouslyGetAnalyticsWebClient()}
        >
          <AnalyticsContext data={analyticsContextData}>
            <SmartCardProvider
              client={smartCardClient}
              key={refreshKey}
              featureFlags={smartCardProviderFeatureFlags}
            >
              <IntlProvider locale={language} messages={messages}>
                <SmartCard
                  onClick={onClick}
                  url={url}
                  {...smartCardProps}
                  platform="web"
                />
              </IntlProvider>
            </SmartCardProvider>
          </AnalyticsContext>
        </FabricAnalyticsListeners>
      </Suspense>
    </ChunkLoadErrorBoundary>
  );
};
